import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { HistorySnapshot } from "@/hooks/useUsageHistory";

const DAY_LABELS = ["", "Mon", "", "Wed", "", "Fri", ""];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  date: string;
  messages: number;
  tokens: number;
}

interface ActivityHeatmapProps {
  history: HistorySnapshot[];
}

function getIntensityColor(count: number, maxVal: number): string {
  if (count === 0) return "bg-background-100 border border-background-200/30";
  const ratio = count / Math.max(maxVal, 1);
  if (ratio <= 0.1) return "bg-accent-100/40";
  if (ratio <= 0.2) return "bg-accent-100/60";
  if (ratio <= 0.35) return "bg-accent-200/70";
  if (ratio <= 0.5) return "bg-accent-300/70";
  if (ratio <= 0.65) return "bg-accent-400/70";
  if (ratio <= 0.8) return "bg-accent-500/70";
  return "bg-accent-500";
}

interface DayCell {
  date: string;
  dayOfWeek: number;
  messages: number;
  tokens: number;
  inRange: boolean;
}

function buildCalendarGrid(history: HistorySnapshot[]): { weeks: DayCell[][]; monthLabels: { weekIdx: number; label: string }[] } {
  const today = new Date();
  const cells: DayCell[] = [];

  const historyMap = new Map<string, HistorySnapshot>();
  for (const h of history) {
    historyMap.set(h.date, h);
  }

  for (let i = 27; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const snap = historyMap.get(dateStr);
    cells.push({
      date: dateStr,
      dayOfWeek: d.getDay() === 0 ? 6 : d.getDay() - 1,
      messages: snap?.messages ?? 0,
      tokens: snap?.tokens ?? 0,
      inRange: i <= history.length + 1,
    });
  }

  const weeks: DayCell[][] = [];
  const monthLabels: { weekIdx: number; label: string }[] = [];
  let currentWeek: DayCell[] = [];

  const startPad = cells[0].dayOfWeek;
  for (let p = 0; p < startPad; p++) {
    currentWeek.push({ date: "", dayOfWeek: p, messages: -1, tokens: 0, inRange: false });
  }

  for (let i = 0; i < cells.length; i++) {
    currentWeek.push(cells[i]);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  for (let w = 0; w < weeks.length; w++) {
    for (const cell of weeks[w]) {
      if (cell.date) {
        const d = new Date(cell.date + "T00:00:00");
        if (d.getDate() <= 7) {
          const label = MONTHS[d.getMonth()];
          if (!monthLabels.some((m) => m.weekIdx === w)) {
            monthLabels.push({ weekIdx: w, label });
          }
        }
        break;
      }
    }
  }

  return { weeks, monthLabels };
}

export default function ActivityHeatmap({ history }: ActivityHeatmapProps) {
  const { t } = useTranslation("dashboard");
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, date: "", messages: 0, tokens: 0 });

  const { weeks, monthLabels } = useMemo(() => buildCalendarGrid(history), [history]);

  const maxMessages = useMemo(() => {
    let max = 0;
    for (const week of weeks) {
      for (const cell of week) {
        if (cell.messages > max) max = cell.messages;
      }
    }
    return max;
  }, [weeks]);

  const totalMessages = useMemo(() => {
    let sum = 0;
    for (const week of weeks) {
      for (const cell of week) {
        if (cell.messages > 0) sum += cell.messages;
      }
    }
    return sum;
  }, [weeks]);

  const activeDays = useMemo(() => {
    let count = 0;
    for (const week of weeks) {
      for (const cell of week) {
        if (cell.messages > 0) count++;
      }
    }
    return count;
  }, [weeks]);

  const handleMouseEnter = useCallback((e: React.MouseEvent, cell: DayCell) => {
    if (!cell.date) return;
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const d = new Date(cell.date + "T00:00:00");
    const label = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
    setTooltip({ visible: true, x: rect.left + rect.width / 2, y: rect.top - 8, date: label, messages: cell.messages, tokens: cell.tokens });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const hasData = weeks.some((w) => w.some((c) => c.messages > 0));

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-heading text-lg text-foreground-950">{t("charts.activityCalendar")}</h3>
          <p className="text-xs text-foreground-500 mt-0.5">
            {t("charts.activitySubtitle", { messages: totalMessages.toLocaleString(), days: activeDays })}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-foreground-400">{t("charts.less")}</span>
          <span className="w-3 h-3 rounded-sm bg-background-100 border border-background-200/30"></span>
          <span className="w-3 h-3 rounded-sm bg-accent-100/40"></span>
          <span className="w-3 h-3 rounded-sm bg-accent-200/70"></span>
          <span className="w-3 h-3 rounded-sm bg-accent-300/70"></span>
          <span className="w-3 h-3 rounded-sm bg-accent-500/70"></span>
          <span className="text-foreground-400">{t("charts.more")}</span>
        </div>
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-calendar-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-foreground-500 text-sm font-medium">{t("charts.buildingHistory")}</p>
          <p className="text-foreground-400 text-xs mt-1 max-w-xs">
            {t("charts.buildingHistoryDesc")}
          </p>
        </div>
      ) : (
        <div className="flex gap-1 overflow-x-auto pb-1">
          <div className="flex flex-col gap-1 pr-2 pt-5 shrink-0">
            {monthLabels.map((m) => (
              <div
                key={m.weekIdx}
                className="text-[9px] font-medium text-foreground-400"
                style={{ marginTop: m.weekIdx === 0 ? 0 : `${(m.weekIdx) * 18}px` }}
              >
                {m.label}
              </div>
            ))}
          </div>

          <div className="shrink-0">
            <div className="flex gap-1 mb-1.5">
              {DAY_LABELS.map((label, i) => (
                <div key={i} className="w-[18px] h-[18px] flex items-center justify-center text-[9px] text-foreground-400 font-medium">
                  {label}
                </div>
              ))}
            </div>

            <div className="relative flex flex-col gap-1">
              {weeks.map((week, wi) => (
                <div key={wi} className="flex gap-1">
                  {week.map((cell, ci) => (
                    <div
                      key={ci}
                      className={`w-[18px] h-[18px] rounded-sm transition-all duration-150 ${
                        cell.messages >= 0
                          ? `cursor-pointer hover:scale-125 hover:z-10 hover:shadow-sm ${getIntensityColor(cell.messages, maxMessages)}`
                          : "bg-transparent"
                      }`}
                      onMouseEnter={(e) => cell.messages >= 0 && handleMouseEnter(e, cell)}
                      onMouseLeave={handleMouseLeave}
                    />
                  ))}
                </div>
              ))}

              {tooltip.visible && (
                <div
                  className="fixed pointer-events-none z-50 bg-foreground-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap"
                  style={{ left: tooltip.x, top: tooltip.y, transform: "translate(-50%, -100%)" }}
                >
                  <p className="font-semibold">{tooltip.date}</p>
                  <p className="text-white/70 text-[11px] mt-0.5">
                    {tooltip.messages.toLocaleString()} msgs &middot; {tooltip.tokens.toLocaleString()} tokens
                  </p>
                  <div
                    className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-full"
                    style={{ width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid oklch(var(--foreground-900))" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-background-200/30 grid grid-cols-2 gap-3">
        <div className="p-3 rounded-xl bg-accent-100/40">
          <p className="text-[10px] text-foreground-500 uppercase tracking-wide mb-0.5">{t("charts.activeDays")}</p>
          <p className="text-foreground-950 font-semibold text-sm">{t("charts.of28", { days: activeDays })}</p>
        </div>
        <div className="p-3 rounded-xl bg-primary-100/40">
          <p className="text-[10px] text-foreground-500 uppercase tracking-wide mb-0.5">{t("charts.avgDaily")}</p>
          <p className="text-foreground-950 font-semibold text-sm">
            {activeDays > 0 ? t("charts.avgDailyMsgs", { msgs: Math.round(totalMessages / activeDays).toLocaleString() }) : "—"}
          </p>
        </div>
      </div>
    </div>
  );
}